import { createFileRoute } from "@tanstack/react-router";
import { ArrowUpIcon } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { Link } from "@tanstack/react-router";
import useAppStore from "@/store";
import { useSimpleForm } from "@/hooks/use-simple-form";
import { Card, CardContent } from "@/components/ui/card";
import { InputField } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useTranslate } from "@/hooks/use-translate";
import ChangeLanguage from "@/components/ui/shared/change-language";

export const Route = createFileRoute("/(onboarding)/auth/login")({
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const store = useAppStore();
  const form = useSimpleForm({
    email: "",
    password: "",
  });
  const { t } = useTranslate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    store.errors.reset();
    store.auth
      .login(form.values.email, form.values.password)
      .then(() => {
        navigate({ to: "/" });
      })
      .catch((error) => store.errors.catch(error))
      .finally(() => {
        store.loading.stop("login");
      });
  };

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-gray-100">
      <div className="flex-1 relative overflow-hidden p-4 lg:p-0">
        <div className="absolute top-4 left-4">
          <img
            src="/assets/images/logo.svg"
            alt="SalesBox Logo"
            width={120}
            height={40}
          />
        </div>
        <div className="hidden lg:block absolute top-20 left-10">
          <Card className="w-64">
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">{t("Total Revenue")}</div>
              <div className="text-2xl font-bold flex items-center">
                100K XAF
                <ArrowUpIcon className="w-4 h-4 text-green-500 ml-2" />
                <svg
                  width="57"
                  height="56"
                  viewBox="0 0 57 56"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="28.1456" cy="28" r="28" fill="#ECF2FF" />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M28.1456 19C28.6979 19 29.1456 19.4477 29.1456 20L29.1464 20.0903C31.3393 20.4914 33.035 22.1853 33.1404 24.2915L33.1456 24.5H31.1456C31.1456 23.4338 30.3235 22.491 29.147 22.1428L29.1464 27.0903C31.4112 27.5046 33.1456 29.2978 33.1456 31.5C33.1456 33.7022 31.4112 35.4954 29.1464 35.9097L29.1456 36C29.1456 36.5523 28.6979 37 28.1456 37C27.5933 37 27.1456 36.5523 27.1456 36L27.1458 35.9099C24.9525 35.5091 23.2563 33.815 23.1508 31.7085L23.1456 31.5H25.1456C25.1456 32.5665 25.9682 33.5095 27.1452 33.8575L27.1458 28.9099C24.8806 28.4959 23.1456 26.7025 23.1456 24.5C23.1456 22.2975 24.8806 20.5041 27.1458 20.0901L27.1456 20C27.1456 19.4477 27.5933 19 28.1456 19ZM29.1469 29.1428L29.147 33.8572C30.3235 33.509 31.1456 32.5662 31.1456 31.5C31.1456 30.4337 30.3234 29.491 29.1469 29.1428ZM25.1456 24.5C25.1456 25.5666 25.9683 26.5096 27.1454 26.8575L27.1452 22.1425C25.9682 22.4905 25.1456 23.4335 25.1456 24.5Z"
                    fill="#1A7DC0"
                  />
                </svg>
              </div>
            </CardContent>
          </Card>
        </div>
        <div className="hidden lg:block absolute top-20 right-10">
          <Card className="w-64">
            <CardContent className="p-4">
              <div className="flex items-center">
                <div className="bg-blue-100 p-2 rounded-full mr-2">
                  <svg
                    className="w-6 h-6 text-blue-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <div className="flex items-center">
                  <div className="text-2xl font-bold">8,458</div>
                  <div className="text-sm text-red-500 ml-2">▼ 3.10%</div>
                </div>
              </div>
              <div className="text-sm text-gray-500 mt-2">
                {t("New Customers")}
              </div>
            </CardContent>
          </Card>
        </div>
        <div className="hidden lg:block absolute bottom-20 right-10">
          <Card className="w-50">
            <CardContent className="p-4">
              <div className="text-2xl font-bold flex items-center">
                42.5k XAF
                <span className="text-sm text-green-500 ml-2">+62%</span>
              </div>
              <div className="text-sm text-gray-500">{t("Sessions")}</div>
              <div className="mt-2">
                <svg
                  width="129"
                  height="93"
                  viewBox="0 0 129 93"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <line
                    x1="2.63391"
                    y1="2.18557e-08"
                    x2="2.63391"
                    y2="93"
                    stroke="#4C4E64"
                    strokeOpacity="0.12"
                    strokeDasharray="6 6"
                  />
                  <line
                    x1="26.4606"
                    y1="2.17903e-08"
                    x2="26.4606"
                    y2="93"
                    stroke="#4C4E64"
                    strokeOpacity="0.12"
                    strokeDasharray="6 6"
                  />
                  <line
                    x1="50.2872"
                    y1="2.17903e-08"
                    x2="50.2872"
                    y2="93"
                    stroke="#4C4E64"
                    strokeOpacity="0.12"
                    strokeDasharray="6 6"
                  />
                  <line
                    x1="74.1139"
                    y1="2.17903e-08"
                    x2="74.1139"
                    y2="93"
                    stroke="#4C4E64"
                    strokeOpacity="0.12"
                    strokeDasharray="6 6"
                  />
                  <line
                    x1="97.9406"
                    y1="2.17903e-08"
                    x2="97.9405"
                    y2="93"
                    stroke="#4C4E64"
                    strokeOpacity="0.12"
                    strokeDasharray="6 6"
                  />
                  <line
                    x1="121.767"
                    y1="2.17903e-08"
                    x2="121.767"
                    y2="93"
                    stroke="#4C4E64"
                    strokeOpacity="0.12"
                    strokeDasharray="6 6"
                  />
                  <path
                    d="M2.13391 90.9431L26.4188 55.4935L50.2454 83.2169L75.4466 36.8597L98.3569 64.5831L121.725 8.22729"
                    stroke="#26C6F9"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                  <circle
                    cx="121.368"
                    cy="10"
                    r="5.5"
                    fill="white"
                    stroke="#26C6F9"
                    strokeWidth="3"
                  />
                </svg>
              </div>
            </CardContent>
          </Card>
        </div>
        <img
          src="/assets/images/login-image.svg"
          alt="3D Character"
          width={1000}
          height={800}
          className="hidden lg:block absolute bottom-0 right-0 max-w-full max-h-full object-contain mb-10 mr-0"
        />
      </div>
      <div className="flex-1 flex items-center justify-center p-4 sm:p-8 bg-white relative">
        <div className="absolute top-4 right-8">
          <ChangeLanguage />
        </div>
        <div className="w-full max-w-md mx-auto">
          <h1 className="text-2xl lg:text-3xl font-bold mb-2">
            {t("Welcome to SaleBox! 👋")}
          </h1>
          <p className="text-gray-500 mb-8">
            {t("Please sign-in to your account and start the adventure")}
          </p>
          <form className="space-y-4" onSubmit={handleLogin}>
            <div>{store.errors.render()}</div>
            <div>
              <InputField
                label={t("Email")}
                name="email"
                value={form.values.email}
                onChange={form.handleChange}
              />
            </div>
            <div>
              <InputField
                label={t("Password")}
                type="password"
                name="password"
                canToggleType={true}
                value={form.values.password}
                onChange={form.handleChange}
              />
              <Link
                to="/auth/password-forgot"
                className="text-primary-600 text-sm font-medium  hover:underline"
              >
                {t("Forgot your password?")}
              </Link>
            </div>

            <Button
              className="w-full"
              type="submit"
              disabled={store.loading.status.login}
              loading={store.loading.status.login}
            >
              {t("Login")}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500 flex justify-center gap-2">
              {t("New on our platform?")}
              <Link
                to="/auth/register"
                className="text-primary-600 font-semibold hover:underline"
              >
                {t("Create an account")}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
